import { useState } from 'react';

import { Badge, Button, Card, Icon, KpiCard } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { abrirAnexo } from '@/comuns/abrirAnexo';
import { textoDaInadimplencia } from '@/comuns/AvisoDeInadimplencia';
import { dataSimples, diaIso, diasEntre, mesEAno, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import type { AssinaturaVistaPelaClinica } from '@/data/cadastros';
import { descricaoDoGateway, pagamentoMenor } from '@/data/gateways';
import { dados } from '@/data/porta';
import type { Fatura, SituacaoDaClinica, SituacaoDaFatura } from '@/data/tipos';
import { autocadastro } from '@/session/autocadastro';
import { atual } from '@/session/sessao';

const SITUACAO_DA_FATURA: Record<SituacaoDaFatura, { rotulo: string; tom: 'success' | 'danger' | 'warning' }> = {
    paga: { rotulo: 'Paga', tom: 'success' },
    vencida: { rotulo: 'Vencida', tom: 'danger' },
    aberta: { rotulo: 'Em aberto', tom: 'warning' },
    /* A T5b passou a registrar o estorno de fatura paga: é devolução, não dívida. */
    estornada: { rotulo: 'Estornada', tom: 'warning' },
};

const SITUACAO_DA_CLINICA: Record<SituacaoDaClinica, string> = {
    ativa: 'Ativa',
    trial: 'Em teste',
    inadimplente: 'Inadimplente',
    suspensa: 'Suspensa',
    cancelada: 'Cancelada',
};

/**
 * Gerenciar assinatura — como a própria clínica vê o que paga, e onde paga.
 *
 * Pagar existe (trilha T9): "Pagar" pede o link ao servidor, que cria a cobrança
 * no gateway ativo se ainda não houver, ou refaz a que venceu. Trocar de plano e
 * cancelar a assinatura continuam com a equipe da Fórmula MED: o protótipo tinha os dois
 * botões, mas nenhum existe como processo — botão que parecesse fazer isso sozinho
 * mentiria. A exceção é o TESTE do autocadastro (0150): cancelá-lo existe como
 * processo (a assinatura sai do gateway e a clínica fecha no último dia), e é da
 * proprietária.
 */
export function AssinaturaDaClinicaAba({ assinatura: a }: { assinatura: AssinaturaVistaPelaClinica }) {
    const aviso = useAviso();
    const hoje = diaIso();
    const dias = a.proximaCobranca ? diasEntre(hoje, a.proximaCobranca) : undefined;
    const alerta = a.inadimplencia ? textoDaInadimplencia(a.inadimplencia) : null;

    const sessao = atual();
    /* Cancelado nesta visita: a aba muda na hora, sem reler a carga — ela não depende
       do roteador, e a faixa do topo relê sozinha ao voltar para a janela. */
    const [canceladaAgora, setCanceladaAgora] = useState<string | null>(null);
    const canceladaEm = a.canceladaEm ?? canceladaAgora ?? undefined;
    const emTeste = a.situacao === 'trial' && !!a.testeAte;
    /* Impersonação só lê: o botão nem aparece para quem entrou para ajudar. */
    const podeCancelarTeste = emTeste && !canceladaEm && sessao?.papel === 'proprietaria' && !sessao.impersonando;
    const [confirmandoCancelamento, setConfirmandoCancelamento] = useState(false);
    const [cancelando, setCancelando] = useState(false);

    const cancelarTeste = async () => {
        if (!sessao?.clinicaId) return;
        setCancelando(true);
        const r = await autocadastro().cancelarTeste(sessao.clinicaId);
        setCancelando(false);
        setConfirmandoCancelamento(false);
        if (!r.ok) {
            aviso({ title: 'Não foi possível cancelar o teste', description: r.erro, tone: 'danger' });
            return;
        }
        setCanceladaAgora(hoje);
        aviso({ title: 'Teste cancelado', description: `Nada será cobrado. A clínica fica aberta até ${dataSimples(r.testeAte)}.`, tone: 'success' });
    };

    /* A aba abre ainda dentro do clique, e só depois recebe o endereço: o link
       pode precisar ser criado no gateway, e `window.open` depois da espera seria
       bloqueado como pop-up. */
    const pagar = async (f: Fatura) => {
        try {
            const abriu = await abrirAnexo(async () => (await dados().linkDePagamento(f.id)).link ?? '');
            if (!abriu) {
                aviso({
                    title: 'A página de pagamento não abriu',
                    description: 'O navegador bloqueou a nova aba, ou o gateway não devolveu link. Use o PIX ou a linha digitável, se aparecerem, ou tente de novo.',
                    tone: 'danger',
                });
            }
        } catch (e) {
            aviso({ title: 'Não foi possível abrir o pagamento', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const copiar = async (rotulo: string, texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            aviso({ title: `${rotulo} copiado`, description: 'cole no aplicativo do banco' });
        } catch {
            aviso({ title: 'Não foi possível copiar', description: texto, tone: 'danger' });
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {a.inadimplencia && alerta && (
                <Alert tone={a.inadimplencia.bloqueadaEm ? 'danger' : 'warning'} dismissible={false} title={alerta.titulo}>
                    {alerta.texto}
                </Alert>
            )}

            {emTeste && (
                <Alert
                    tone="info"
                    dismissible={false}
                    title={canceladaEm ? 'Teste cancelado' : `Teste do plano ${a.plano} até ${dataSimples(a.testeAte)}`}
                >
                    {canceladaEm
                        ? `Nada será cobrado. A clínica fica aberta até ${dataSimples(a.testeAte)} e fecha nesse dia.`
                        : a.formaDePagamento === 'cartao' && a.precoCentavos !== undefined
                            ? `Nesse dia, ${moeda(a.precoCentavos)} no cartão${a.cartaoFinal ? ` final ${a.cartaoFinal}` : ''}. Cancelando antes, nada é cobrado.`
                            : 'Cancelando antes do último dia, nada é cobrado.'}
                </Alert>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
                <KpiCard
                    label="Plano atual" value={a.plano || '—'}
                    hint={a.limiteMembros ? `até ${a.limiteMembros} profissionais` : `ciclo ${a.ciclo}`}
                />
                <KpiCard
                    label={a.ciclo === 'anual' ? 'Valor anual' : 'Valor mensal'}
                    value={a.precoCentavos === undefined ? 'A definir' : moeda(a.precoCentavos)}
                    hint={a.precoCentavos === undefined ? 'a Fórmula MED ainda não fixou o preço' : `ciclo ${a.ciclo}`}
                />
                <KpiCard
                    label="Próxima cobrança" value={dataSimples(a.proximaCobranca)}
                    hint={dias === undefined ? (canceladaEm ? `cancelada em ${dataSimples(canceladaEm)}` : 'sem cobrança marcada') : dias >= 0 ? `em ${dias} dias` : `há ${-dias} dias`}
                />
                <KpiCard label="Situação" value={SITUACAO_DA_CLINICA[a.situacao]} hint={a.inicio ? `desde ${dataSimples(a.inicio)}` : undefined} />
            </div>

            <Card title="Faturas" subtitle="Histórico de cobrança da assinatura. A fatura em aberto se paga por aqui." padding={0}>
                {a.faturas.length === 0 ? (
                    <p style={{ margin: 0, padding: '18px 20px', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                        Nenhuma fatura ainda. Elas nascem a cada competência depois que o plano tiver preço.
                    </p>
                ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {a.faturas.map((f, i) => {
                            const mes = mesEAno(`${f.competencia.slice(0, 7)}-01`);
                            const emAberto = f.situacao === 'aberta' || f.situacao === 'vencida';
                            const pelo = f.pagoPor === 'gateway' && f.gateway ? ` pelo ${descricaoDoGateway(f.gateway).nome}` : '';
                            const menor = pagamentoMenor(f);
                            return (
                                <li key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '13px 20px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                                    <Icon name="receipt" size={16} style={{ color: 'var(--neutral-500)' }} />
                                    <span style={{ flex: 1, minWidth: 160 }}>
                                        <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                                            {f.tipo === 'avulsa' && f.descricao ? f.descricao : mes}
                                        </span>
                                        <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                            {f.pagoEm ? `Paga em ${dataSimples(f.pagoEm.slice(0, 10))}${pelo}` : `Vence em ${dataSimples(f.vencimento)}`}
                                        </span>
                                        {/* Pagamento menor não quita (0118): a fatura segue em aberto, e
                                            o link antigo cobraria o valor inteiro — por isso não há "Pagar". */}
                                        {menor && (
                                            <span style={{ display: 'block', marginTop: 2, fontSize: 12.5, color: 'var(--warning-strong, var(--warning))' }}>
                                                Recebemos {moeda(menor.recebidoCentavos)}; faltam {moeda(menor.faltaCentavos)} para quitar esta fatura.
                                            </span>
                                        )}
                                        {menor && (
                                            <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                                Fale com a Fórmula MED para acertar a diferença.
                                            </span>
                                        )}
                                    </span>
                                    <Badge tone={SITUACAO_DA_FATURA[f.situacao].tom} dot>{SITUACAO_DA_FATURA[f.situacao].rotulo}</Badge>
                                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(f.valorCentavos)}</span>
                                    {emAberto && f.valorCentavos > 0 && !menor && (
                                        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <Button size="sm" variant={f.situacao === 'vencida' ? undefined : 'secondary'} onClick={() => { void pagar(f); }} aria-label={`Pagar a fatura de ${mes}`}>
                                                Pagar
                                            </Button>
                                            {f.cobrancaPix && (
                                                <Button size="sm" variant="ghost" onClick={() => { void copiar('PIX copia-e-cola', f.cobrancaPix ?? ''); }}>Copiar PIX</Button>
                                            )}
                                            {f.cobrancaLinhaDigitavel && (
                                                <Button size="sm" variant="ghost" onClick={() => { void copiar('Linha digitável', f.cobrancaLinhaDigitavel ?? ''); }}>Copiar linha digitável</Button>
                                            )}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            <Card title={emTeste ? 'Cancelar o teste' : 'Alterar plano ou cancelar'} padding={20}>
                {emTeste ? (
                    <>
                        <p style={{ margin: '0 0 14px', fontFamily: 'var(--font-ui)', fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)', maxWidth: '62ch' }}>
                            Cancelar dentro do teste não cobra nada: a assinatura sai do meio de pagamento na hora, e a clínica fica aberta até {dataSimples(a.testeAte)}.
                        </p>
                        {podeCancelarTeste ? (
                            <Button variant="secondary" onClick={() => setConfirmandoCancelamento(true)}>Cancelar o teste</Button>
                        ) : !canceladaEm && (
                            <Alert tone="info" dismissible={false}>Só a proprietária da clínica cancela o teste.</Alert>
                        )}
                    </>
                ) : (
                    <>
                        <p style={{ margin: '0 0 14px', fontFamily: 'var(--font-ui)', fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)', maxWidth: '62ch' }}>
                            O acesso continua até o fim do ciclo pago. Os dados clínicos ficam disponíveis para exportação por 90 dias após o encerramento.
                        </p>
                        <Alert tone="info" title="Troca de plano e cancelamento são feitos pela equipe da Fórmula MED">
                            Fale com o suporte: a mudança passa por quem opera a cobrança, e aparece aqui assim que for registrada.
                        </Alert>
                    </>
                )}
            </Card>

            <Modal
                open={confirmandoCancelamento}
                tone="danger"
                title="Cancelar o teste?"
                description={`Nada será cobrado. A clínica fica aberta até ${dataSimples(a.testeAte)} e fecha nesse dia.`}
                saveLabel={cancelando ? 'Cancelando…' : 'Cancelar o teste'}
                discardLabel="Manter o teste"
                onSave={cancelarTeste}
                onClose={() => { if (!cancelando) setConfirmandoCancelamento(false); }}
            />
        </div>
    );
}
